import React, {
  ElementRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { ReservationTimeFrameProps } from 'types/med'
import { Checkbox, FormControlLabel, MenuItem, Select } from '@mui/material'
import { CheckBoxOutlineBlank } from '@mui/icons-material'
import { isEqual } from 'lodash'
import {
  DayOfWeekCheckStatus,
  getTimeEnd,
  INITIAL_SCROLL_TOP,
} from 'ui/organisms/ReservationFrameSetting/RegularReservationFrameSetting'
import {
  ReservationFrameSettingCellReadOnly,
  ReservationSettingOfTimeCell,
  ReservationFrameTimes as ReservationFrameTimesComponent,
} from './ReservationFrameSettingCell'
import { StartEnd } from 'ui/utils/AvailableDateTimes'
import DatePicker from 'ui/partials/DatePicker'
import CloseIconButton from 'ui/shorthands/CloseIconButton'
import {
  formatDateString,
  formatYearDateJpDayOfWeek,
  parseDateString,
} from 'ui/utils/dateFormatUtils'
import { startOfJstToDay } from 'ui/utils/jstDatetime'
import { buildStartEndList } from 'ui/utils/AvailableDateTimes'
import { ReservationFrameUnitMin } from 'types/common'

const isValidDates = (start: Date | null, end: Date | null): boolean => {
  return !!(
    start &&
    end &&
    start.valueOf() <= end.valueOf() && // NOTE: endDateはstartDateの後の日付であること
    startOfJstToDay.valueOf() <= start.valueOf()
  )
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: '100%',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    '&:not(:first-child)': {
      marginTop: theme.spacing(1),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  closeButton: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  calendarItems: {
    paddingLeft: theme.spacing(2),
  },
  readOnlyDates: {
    fontSize: theme.typography.fontSize,
    lineHeight: '16px',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
    padding: theme.spacing(2),
  },
  scrollContainer: {
    height: '402px',
    display: 'grid',
    gridTemplateColumns: '48px 1fr',
    overflowY: 'auto',
    border: `1px solid ${theme.palette.divider}`,
  },
  scrollContainerReadOnly: {
    height: 'auto',
    maxHeight: '306px',
  },
  subGrid: {
    display: 'grid',
    gridAutoRows: '44px',
  },
  row: {
    border: `0 solid ${theme.palette.divider}`,
    '&:not(:first-child)': {
      borderLeftWidth: '1px',
    },
  },
  valueCell: {
    paddingLeft: '58px',
    justifyContent: 'flex-start',
  },
  applyDropdownContainer: {
    margin: 0,
  },
  applyDropdown: {
    height: '36px',
    boxSizing: 'border-box',
    width: '115px',
    marginLeft: '16px',
    fontSize: theme.typography.fontSize,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1.6, 2),
    alignItems: 'center',
  },
  footerCheckboxContainer: {
    flexGrow: 1,
  },
}))

const ReadOnlyDates: React.FC<{ start: Date; end: Date }> = ({
  start,
  end,
}) => {
  const { classes } = useStyles()
  const startText = formatYearDateJpDayOfWeek(start)
  const endText = formatYearDateJpDayOfWeek(end)
  return (
    <span className={classes.readOnlyDates}>
      {startText !== endText ? `${startText}〜${endText}` : startText}
    </span>
  )
}

export type EditableIndividualReservationSettingProps = {
  id: number
  reservation_frames: Array<ReservationTimeFrameProps>
  start_date: string | null
  end_date: string | null
}

type IndividualReservationSettingListItemProps = {
  /** 予約設定一覧 */
  reservationSetting: EditableIndividualReservationSettingProps

  /** 内容が更新された場合に発火 */
  onChange?: (
    reservationSetting: EditableIndividualReservationSettingProps
  ) => void

  /** 削除された場合に発火 */
  onDelete?: () => void

  /** 参照のみのモードを指定 */
  readOnly?: boolean

  /** 予約枠の時間 */
  unit: ReservationFrameUnitMin
}

const IndividualReservationSettingListItem: React.FC<
  IndividualReservationSettingListItemProps
> = ({
  reservationSetting,
  onChange,
  onDelete,
  readOnly,
  unit,
}: IndividualReservationSettingListItemProps) => {
  const { t } = useTranslation()

  const { classes, cx } = useStyles()

  const startEnds: StartEnd[] = useMemo(() => {
    return buildStartEndList({
      from: '00:00',
      to: '24:00',
      unit,
    })
  }, [unit])

  const maxNumber = (20 * unit) / 30

  const onChangeRef = useRef(onChange)

  const [checkStatuses, setCheckStatuses] = useState<DayOfWeekCheckStatus>({})

  const [allChecked, indeterminate] = useMemo(() => {
    const checkedLength = Object.values(checkStatuses).filter(
      (checked) => checked
    ).length
    const anyChecked = checkedLength > 0
    const isIndeterminate = checkedLength !== startEnds.length
    return [anyChecked, anyChecked && isIndeterminate]
  }, [checkStatuses, startEnds.length])

  const [applyDropdownSelection, _setApplyDropdownSelection] =
    useState<number>(0)

  // 選択項目に適用を実行する
  const setApplyDropdownSelection = useCallback(
    (number_of_people: number) => {
      const checkedTimes = Object.entries(checkStatuses)
        .filter(([, checked]) => checked)
        .map(([timeStart]) => timeStart)

      // チェックされていない既存のReservationTimeFrameProps
      const unCheckedExistingFrames: ReservationTimeFrameProps[] =
        reservationSetting.reservation_frames.filter(
          ({ start_time }) => !checkedTimes.includes(start_time)
        ) ?? []

      // チェックされた時間のReservationTimeFrameProps
      const checkedFrames: ReservationTimeFrameProps[] = checkedTimes.map(
        (start_time) => ({
          start_time: start_time,
          end_time: getTimeEnd(start_time, unit),
          number_of_people,
        })
      )

      const reservation_frames = unCheckedExistingFrames
        .concat(checkedFrames)
        .filter((frame) => frame.number_of_people > 0) // NOTE: number_of_people === 0 は弾く

      const newSetting = { ...reservationSetting, reservation_frames }

      // NOTE: 変更があるときのみ適用
      if (!isEqual(newSetting, reservationSetting)) {
        onChangeRef.current?.({ ...reservationSetting, reservation_frames })
      }
      _setApplyDropdownSelection(number_of_people)
    },
    [checkStatuses, reservationSetting, unit]
  )

  const scrollRef = useRef<ElementRef<'div'> | null>()
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = INITIAL_SCROLL_TOP
    }
  }, [])

  const setAllCheck = (checked: boolean) => {
    const newCheckStatuses = Object.fromEntries(
      startEnds.map(({ start }) => [start, checked])
    )

    setCheckStatuses(newCheckStatuses)
  }

  // NOTE: チェックが変わった際に人数を適用
  useEffect(() => {
    setApplyDropdownSelection(
      maxNumber > applyDropdownSelection ? applyDropdownSelection : maxNumber
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkStatuses, maxNumber])

  const { start_date, end_date } = reservationSetting

  const [startDate, setStartDate] = useState<Date | null>(
    start_date ? parseDateString(start_date) : null
  )
  const [endDate, setEndDate] = useState<Date | null>(
    end_date ? parseDateString(end_date) : null
  )

  useEffect(() => {
    if (
      (startDate && !endDate) ||
      (startDate && endDate && startDate > endDate)
    ) {
      setEndDate(startDate)
      return
    }
    if (!isValidDates(startDate, endDate)) {
      return
    }
    const newReservationSetting = {
      ...reservationSetting,
      start_date: formatDateString(startDate!),
      end_date: formatDateString(endDate!),
    }

    // 変更がある場合のみonChangeを呼ぶ
    if (!isEqual(newReservationSetting, reservationSetting)) {
      onChangeRef.current?.(newReservationSetting)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  const filteredReservationFrameTimes = startEnds.filter((time) => {
    return readOnly
      ? !!reservationSetting.reservation_frames.find(
          (frames) => time.start === frames.start_time
        )
      : true
  })

  const frameMap = new Map<string, ReservationTimeFrameProps>()
  reservationSetting.reservation_frames.forEach((data) => {
    frameMap.set(data.start_time, data)
  })

  return startEnds.length ? (
    <div className={classes.root}>
      <div className={classes.header}>
        {readOnly ? (
          <ReadOnlyDates start={startDate!} end={endDate!} />
        ) : (
          <>
            <div className={classes.closeButton}>
              <CloseIconButton onClick={onDelete} />
            </div>
            <div className={classes.calendarItems}>
              <DatePicker
                label={t('開始日')}
                onDateChange={setStartDate}
                date={startDate}
                invalidDateMessage={t('validation.日付の形式が不正です')}
                maxDateMessage={t(
                  'validation.終了日より先の日付は指定できません'
                )}
                minDate={startOfJstToDay}
                minDateMessage={t('validation.過去の日付は指定できません')}
              />
            </div>
            <div className={classes.calendarItems}>
              <DatePicker
                label={t('終了日')}
                onDateChange={setEndDate}
                date={endDate}
                minDate={startDate}
                invalidDateMessage={t('日付の形式が不正です')}
                minDateMessage={t('開始日より前の日付は指定できません')}
              />
            </div>
          </>
        )}
      </div>
      {!readOnly && (
        <div className={classes.footer}>
          <div className={classes.footerCheckboxContainer}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={allChecked}
                  indeterminate={indeterminate}
                  onChange={(_, checked) => setAllCheck(checked)}
                  icon={<CheckBoxOutlineBlank color="action" />}
                />
              }
              label={t('全て選択')}
            />
          </div>
          <div>
            <FormControlLabel
              control={
                <Select
                  displayEmpty
                  variant="outlined"
                  defaultValue={0}
                  value={applyDropdownSelection}
                  className={classes.applyDropdown}
                >
                  {/* NOTE: 同じ項目が選択された場合にonChangeで選択が取れないため、MenuItemのonClickを利用する */}
                  <MenuItem
                    value={0}
                    onClick={() => setApplyDropdownSelection(0)}
                  >
                    {t('なし')}
                  </MenuItem>
                  {[...new Array(maxNumber)].map((_, i) => {
                    const value = i + 1
                    return (
                      <MenuItem
                        key={i}
                        value={value}
                        onClick={() => setApplyDropdownSelection(value)}
                      >
                        {i + 1}人
                      </MenuItem>
                    )
                  })}
                </Select>
              }
              label={t('選択項目に適用')}
              labelPlacement="start"
              className={classes.applyDropdownContainer}
            />
          </div>
        </div>
      )}
      <div
        className={cx(
          classes.scrollContainer,
          readOnly && classes.scrollContainerReadOnly
        )}
        ref={(ref) => (scrollRef.current = ref)}
      >
        <ReservationFrameTimesComponent
          values={readOnly ? filteredReservationFrameTimes : startEnds}
        />
        <div className={cx(classes.subGrid, classes.row)}>
          {filteredReservationFrameTimes.map((time) => {
            const frame = frameMap.get(time.start) || undefined
            return readOnly ? (
              <ReservationFrameSettingCellReadOnly
                key={time.start}
                number={frame?.number_of_people || 0}
              />
            ) : (
              <ReservationSettingOfTimeCell
                key={time.start}
                time={time}
                maxNumber={maxNumber}
                checked={checkStatuses[time.start] ?? false}
                reservationTimeFrame={frame}
                onChangeCheckStatus={(status) =>
                  setCheckStatuses((prevState) => ({ ...prevState, ...status }))
                }
                onChange={(timeFrame) => {
                  const newReservationFrames =
                    reservationSetting.reservation_frames
                      .filter(
                        ({ start_time }) => start_time !== timeFrame.start_time
                      )
                      .concat(timeFrame)
                      .filter((frame) => frame.number_of_people > 0) // NOTE: number_of_people == 0 は除外

                  onChange?.({
                    ...reservationSetting,
                    reservation_frames: newReservationFrames,
                  })
                }}
              />
            )
          })}
        </div>
      </div>
    </div>
  ) : null
}

export default IndividualReservationSettingListItem
